// Client layer (TS-2.5.03) - UC-5/UC-22/UC-23, Contributor+ wizard. Owns the upload -> preview ->
// commit -> result sequence over ingestion.ts, keeping the uploaded File and previewKey in state
// between steps (the commit call re-sends the same file alongside the preview's key, per
// ingestion.ts's own contract - the server does not retain the uploaded bytes between the two
// calls). The dual-run comparison screen is a fully independent tool below the wizard.
import { useState } from "react";
import { ApiError } from "../../api/ApiError";
import type { ImportPreview, ImportResult } from "../../api/generated/contracts";
import { ingestionApi } from "../../api/ingestion";
import { ImportCompareForm } from "./ImportCompareForm";
import { ImportPreviewPanel } from "./ImportPreviewPanel";
import { ImportResultSummary } from "./ImportResultSummary";
import { ImportUploadForm } from "./ImportUploadForm";
import { strings } from "../../strings";

type ImportStep = "upload" | "preview" | "result";

const S = strings.import;

export function ImportPage() {
  const [step, setStep] = useState<ImportStep>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [sourceProfileCode, setSourceProfileCode] = useState<string | undefined>(undefined);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<ApiError | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);

  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  async function handleUpload(nextFile: File, nextSourceProfileCode: string | undefined) {
    setUploading(true);
    setUploadError(null);
    try {
      const next = await ingestionApi.previewWorkbook(nextFile, nextSourceProfileCode);
      setFile(nextFile);
      setSourceProfileCode(nextSourceProfileCode);
      setPreview(next);
      setStep("preview");
    } catch (cause) {
      setUploadError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The workbook could not be previewed."));
    } finally {
      setUploading(false);
    }
  }

  async function handleCommit(acceptWarnings: boolean) {
    if (!file || !preview) return;
    setCommitting(true);
    setCommitError(null);
    try {
      const next = await ingestionApi.commitWorkbook(file, preview.previewKey, acceptWarnings, sourceProfileCode);
      setResult(next);
      setStep("result");
    } catch (cause) {
      setCommitError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The import could not be committed."));
    } finally {
      setCommitting(false);
    }
  }

  function reset() {
    setStep("upload");
    setFile(null);
    setSourceProfileCode(undefined);
    setPreview(null);
    setResult(null);
    setUploadError(null);
    setCommitError(null);
  }

  return (
    <section className="eg-import">
      <h1>{S.heading}</h1>

      {step === "upload" && (
        <>
          <ImportUploadForm submitting={uploading} onSubmit={(...args) => void handleUpload(...args)} />

          {uploadError && uploadError.kind === "payloadTooLarge" && (
            <div role="alert">
              <p>{S.upload.tooLargeTitle}</p>
              <p>{S.upload.tooLargeBody}</p>
            </div>
          )}

          {uploadError && uploadError.kind !== "payloadTooLarge" && (
            <div role="alert">
              <p>{S.upload.errorTitle}</p>
              <p>{uploadError.message}</p>
            </div>
          )}
        </>
      )}

      {step === "preview" && preview && (
        <>
          <ImportPreviewPanel preview={preview} committing={committing} onCommit={(...args) => void handleCommit(...args)} />
          {commitError && (
            <div role="alert">
              <p>{S.preview.errorTitle}</p>
              <p>{commitError.message}</p>
            </div>
          )}
        </>
      )}

      {step === "result" && result && <ImportResultSummary result={result} onStartAnother={reset} />}

      <ImportCompareForm />
    </section>
  );
}
